// The batch id is the client's claim to the server that "this is the same
// write you already did". Getting it wrong is silent data loss, so it lives
// alone here where it can be tested without a network client.
//
// THE RULE: two batches share an id if and only if they would produce the
// same writes.
//
// It used to be `lastSyncAt` plus the block and tap counts, which is a proxy
// for the content rather than the content. Two collisions were reproduced
// against the real database on 2026-09-10:
//
//   1. A note added after a lost response changes neither count, so the retry
//      reused the id, was told `duplicate`, and the note was pruned having
//      never been written. Notes carry the only free text an observer cannot
//      reconstruct later.
//   2. Ending a session closes the open block without changing either count,
//      so the close was pruned the same way and the block stayed open forever.
//
// The asymmetry is the design. A false MISS is harmless: every row is
// idempotent on its primary key and simply lands again. A false HIT destroys
// data. Hashing the payload biases hard toward the harmless side.

/** SHA-256 over the exact payload being sent, not over a summary of it. */
export async function batchIdFor(sessionId: string, payload: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  return `b-${sessionId.slice(0, 8)}-${hex.slice(0, 32)}`;
}
